import { useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Seo } from '@/components/Seo';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Swell Breaks';
  }, []);

  return (
    <Layout>
      <Seo
        title="Privacy Policy | Swell Breaks"
        description="How Swell Breaks collects, uses and protects your personal information."
        path="/privacy-policy"
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Shield className="h-4 w-4" />
                Privacy
              </div>
              <h1 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-4">
                Privacy Policy
              </h1>
              <p className="text-muted-foreground">
                Last Updated: 23 June 2026
              </p>
            </div>

            <Card className="bg-card/80 backdrop-blur-sm border-border/20">
              <CardContent className="p-8 md:p-12">
                <div className="prose prose-invert max-w-none">
                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Introduction</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Swell Breaks Ltd ("Swell Breaks", "we", "our", or "us") is committed to protecting your privacy and handling your personal information responsibly.</p>
                    <p>This Privacy Policy explains how we collect, use, store, and protect information when you use the Swell Breaks website, mobile applications, and related services (collectively, the "Services").</p>
                    <p>By using our Services, you agree to the practices described in this Privacy Policy.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Company Information</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-1 mb-8">
                    <p className="font-semibold text-foreground">Swell Breaks Ltd</p>
                    <p>Company Number: 17118943</p>
                    <p>Contact Email: <a href="mailto:Finn.Turley@swellbreaks.com" className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors">Finn.Turley@swellbreaks.com</a></p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Information We Collect</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-4 mb-8">
                    <h3 className="text-foreground font-semibold text-lg">Information You Provide</h3>
                    <p>We may collect information that you voluntarily provide, including:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Name</li>
                      <li>Email address</li>
                      <li>Account information</li>
                      <li>Profile information</li>
                      <li>Travel preferences</li>
                      <li>Surf experience and skill level</li>
                      <li>Favourite destinations</li>
                      <li>Contact form submissions</li>
                      <li>Communications with us</li>
                    </ul>

                    <h3 className="text-foreground font-semibold text-lg pt-2">Information Collected Automatically</h3>
                    <p>When you use our Services, we may automatically collect:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>IP address</li>
                      <li>Device information</li>
                      <li>Browser type</li>
                      <li>Operating system</li>
                      <li>Pages visited</li>
                      <li>Session duration</li>
                      <li>Referral sources</li>
                      <li>General location information</li>
                    </ul>

                    <h3 className="text-foreground font-semibold text-lg pt-2">Cookies and Similar Technologies</h3>
                    <p>We may use cookies and similar technologies to:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Maintain account sessions</li>
                      <li>Improve website functionality</li>
                      <li>Understand how users interact with our Services</li>
                      <li>Improve performance and user experience</li>
                    </ul>
                    <p>You can manage cookie preferences through your browser settings.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">How We Use Information</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>We use collected information to:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Provide and improve our Services</li>
                      <li>Create and manage user accounts</li>
                      <li>Deliver destination recommendations</li>
                      <li>Personalise user experiences</li>
                      <li>Respond to enquiries</li>
                      <li>Communicate important updates</li>
                      <li>Monitor and improve platform performance</li>
                      <li>Prevent fraud and abuse</li>
                      <li>Comply with legal obligations</li>
                    </ul>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Legal Basis for Processing</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Where applicable under UK GDPR and EU GDPR, we process information on the basis of:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>User consent</li>
                      <li>Contractual necessity</li>
                      <li>Legal obligations</li>
                      <li>Legitimate business interests</li>
                      <li>Protection of vital interests</li>
                    </ul>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Affiliate Links and Third-Party Services</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Swell Breaks may contain affiliate links and references to third-party accommodation providers, surf camps, booking platforms, airlines, and travel services.</p>
                    <p>When you leave our platform and visit a third-party website, that website's privacy policy applies. We are not responsible for the privacy practices of third-party services.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Analytics</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>We may use analytics and performance monitoring tools to better understand how visitors use our platform and improve the user experience.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Data Sharing</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>We do not sell personal information.</p>
                    <p>We may share information with:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Service providers that help operate our platform</li>
                      <li>Analytics providers</li>
                      <li>Cloud hosting providers</li>
                      <li>Professional advisers</li>
                      <li>Legal authorities when required by law</li>
                    </ul>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Data Retention</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>We retain information only for as long as necessary to:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Provide our Services</li>
                      <li>Meet legal obligations</li>
                      <li>Resolve disputes</li>
                      <li>Protect our legal rights</li>
                    </ul>
                    <p>When information is no longer required, it will be securely deleted or anonymised.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Your Rights</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Depending on your location, you may have the right to:</p>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Access your personal information</li>
                      <li>Correct inaccurate information</li>
                      <li>Request deletion</li>
                      <li>Restrict processing</li>
                      <li>Object to processing</li>
                      <li>Request data portability</li>
                      <li>Withdraw consent</li>
                    </ul>
                    <p>Requests may be submitted to: <a href="mailto:Finn.Turley@swellbreaks.com" className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors">Finn.Turley@swellbreaks.com</a></p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Children's Privacy</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>Our Services are not intended for children under the age of 13.</p>
                    <p>We do not knowingly collect personal information from children.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Security</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>We implement reasonable technical and organisational measures to protect personal information from unauthorised access, disclosure, misuse, loss, or alteration.</p>
                    <p>However, no internet-based service can be guaranteed to be completely secure.</p>
                  </div>

                  <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">Changes to This Policy</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 mb-8">
                    <p>We may update this Privacy Policy from time to time.</p>
                    <p>Updates will be posted on this page with a revised "Last Updated" date.</p>
                  </div>
                </div>

                <Separator className="my-10 bg-border/30" />

                <div className="text-center text-sm text-muted-foreground">
                  <p className="mb-1">If you have any questions about this Privacy Policy, please contact us at</p>
                  <a
                    href="mailto:Finn.Turley@swellbreaks.com"
                    className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
                  >
                    Finn.Turley@swellbreaks.com
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;
